import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cleave from "cleave.js/react";
import debounce from "lodash/debounce";
import { FormikProps } from "formik";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Collapse,
  FormFeedback,
  FormText,
  Input,
  Label,
  Row,
} from "reactstrap";
import { CLIENT_OPERATIONAL_NOTES_MAX_LENGTH } from "../../../../helpers/clientNotes";
import {
  CLIENT_PREF_MAX_NOTE,
  CLIENT_PREF_MAX_SHORT,
} from "../../../../helpers/clientServicePreferences";
import {
  checkPhone,
  CLIENT_DOCUMENT_TYPES,
  CLIENT_GENDERS,
} from "../../../../services/clientService";
import {
  getProfessionals,
  Professional,
} from "../../../../services/professionalService";
import { ClientFormValues } from "./ClientAddressFields";

const PHONE_CHECK_DEBOUNCE_MS = 400;
const MIN_PHONE_DIGITS = 10;

export type ClientFormApiError = {
  code?: string;
  data?: string;
  details?: Record<string, string[]>;
};

export const isClientPhoneDuplicateError = (error: ClientFormApiError): boolean => {
  if (error.code === "CLIENT_PHONE_EXISTS") {
    return true;
  }

  const phoneErrors = error.details?.phone_number;

  return Array.isArray(phoneErrors) && phoneErrors.length > 0;
};

export const applyClientPhoneDuplicateError = (
  formik: FormikProps<ClientFormValues>,
  error: ClientFormApiError,
  t: TFunction
): boolean => {
  if (!isClientPhoneDuplicateError(error)) {
    return false;
  }

  formik.setFieldError("phone_display", t("studio.clients.form.phone_duplicate"));
  formik.setFieldTouched("phone_display", true, false);

  return true;
};

export const isBrazilianClientContext = (
  locale: string,
  country?: string | null
): boolean => locale === "pt-BR" || (country ?? "BR").toUpperCase() === "BR";

interface ClientFormProps {
  formik: FormikProps<ClientFormValues>;
  editingClientId?: number | null;
}

const ClientForm: React.FC<ClientFormProps> = ({ formik, editingClientId = null }) => {
  const { t, i18n } = useTranslation();
  const [duplicateClientId, setDuplicateClientId] = useState<number | null>(null);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [contactPrefsOpen, setContactPrefsOpen] = useState(() => {
    try {
      const stored = window.localStorage.getItem(
        "adatis.clients.contactPrefsOpen"
      );
      if (stored === "1") {
        return true;
      }
      if (stored === "0") {
        return false;
      }
    } catch {
      // ignore
    }
    return typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : true;
  });
  const latestPhoneRef = useRef("");

  useEffect(() => {
    let cancelled = false;

    getProfessionals({ per_page: 100 })
      .then((response) => {
        if (!cancelled) {
          setProfessionals(response.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfessionals([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const documentType = formik.values.document_type;
  const isBrazil = isBrazilianClientContext(i18n.language, formik.values.country);
  const useCpfMask = documentType === "cpf" && isBrazil;

  const documentNumberError =
    formik.touched.document_number_display && formik.errors.document_number_display
      ? String(formik.errors.document_number_display)
      : null;
  const documentTypeError =
    formik.touched.document_type && formik.errors.document_type
      ? String(formik.errors.document_type)
      : null;

  const documentNumberLabel =
    isBrazil && documentType === "cpf"
      ? t("studio.clients.fields.document_number.cpf")
      : t("studio.clients.fields.document_number");

  const documentNumberPlaceholder = useCpfMask
    ? t("studio.clients.fields.document_number.placeholder_cpf")
    : t("studio.clients.fields.document_number.placeholder_generic");

  const phoneDuplicateMessage = t("studio.clients.form.phone_duplicate");
  const phoneFieldError =
    formik.touched.phone_display && formik.errors.phone_display
      ? String(formik.errors.phone_display)
      : duplicateClientId
        ? phoneDuplicateMessage
        : null;
  const phoneFieldInvalid = Boolean(phoneFieldError);

  const runPhoneCheck = useCallback(
    async (phoneDisplay: string) => {
      const digits = phoneDisplay.replace(/\D/g, "");

      if (digits.length < MIN_PHONE_DIGITS) {
        setDuplicateClientId(null);
        return;
      }

      latestPhoneRef.current = digits;

      try {
        const response = await checkPhone({
          phone_number: digits,
          country_code: "55",
          ...(editingClientId ? { client_id: editingClientId } : {}),
        });

        if (latestPhoneRef.current !== digits) {
          return;
        }

        setDuplicateClientId(response.data.exists ? response.data.client_id : null);
      } catch {
        if (latestPhoneRef.current === digits) {
          setDuplicateClientId(null);
        }
      }
    },
    [editingClientId]
  );

  const debouncedPhoneCheck = useMemo(
    () => debounce((phoneDisplay: string) => void runPhoneCheck(phoneDisplay), PHONE_CHECK_DEBOUNCE_MS),
    [runPhoneCheck]
  );

  useEffect(() => {
    debouncedPhoneCheck(formik.values.phone_display);

    return () => {
      debouncedPhoneCheck.cancel();
    };
  }, [formik.values.phone_display, debouncedPhoneCheck]);

  useEffect(() => {
    setDuplicateClientId(null);
    latestPhoneRef.current = "";
  }, [editingClientId]);

  return (
    <>
      {duplicateClientId ? (
        <Alert color="warning" className="border-0 bg-warning-subtle text-warning mb-3">
          <i className="ri-information-line me-2 align-middle" />
          {phoneDuplicateMessage}{" "}
          <Link to={`/clients/${duplicateClientId}`} className="alert-link fw-medium">
            {t("studio.clients.form.phone_duplicate_link")}
          </Link>
        </Alert>
      ) : null}

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Label htmlFor="client-phone">{t("clients.fields.phone.label")}</Label>
          <Cleave
            id="client-phone"
            className={`form-control${phoneFieldInvalid ? " is-invalid" : ""}`}
            placeholder={t("clients.fields.phone.placeholder")}
            options={{
              delimiters: ["(", ") ", "-"],
              blocks: [0, 2, 5, 4],
              numericOnly: true,
            }}
            value={formik.values.phone_display}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              formik.setFieldValue("phone_display", event.target.value);
              if (formik.errors.phone_display) {
                formik.setFieldError("phone_display", undefined);
              }
            }}
            onBlur={() => formik.setFieldTouched("phone_display", true)}
          />
          {phoneFieldError ? (
            <div className="invalid-feedback d-block">{phoneFieldError}</div>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-secondary-phone">
            {t("clients.fields.secondary_phone.label")}
          </Label>
          <Cleave
            id="client-secondary-phone"
            className={`form-control${
              formik.touched.secondary_phone_display && formik.errors.secondary_phone_display
                ? " is-invalid"
                : ""
            }`}
            placeholder={t("clients.fields.phone.placeholder")}
            options={{
              delimiters: ["(", ") ", "-"],
              blocks: [0, 2, 5, 4],
              numericOnly: true,
            }}
            value={formik.values.secondary_phone_display}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              formik.setFieldValue("secondary_phone_display", event.target.value)
            }
            onBlur={() => formik.setFieldTouched("secondary_phone_display", true)}
          />
          {formik.touched.secondary_phone_display && formik.errors.secondary_phone_display ? (
            <div className="invalid-feedback d-block">
              {String(formik.errors.secondary_phone_display)}
            </div>
          ) : (
            <FormText muted className="d-block mt-1">
              {t("clients.fields.secondary_phone.hint")}
            </FormText>
          )}
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Label htmlFor="client-email">{t("clients.fields.email.label")}</Label>
          <Input
            id="client-email"
            name="email"
            type="email"
            placeholder={t("clients.fields.email.placeholder")}
            value={formik.values.email ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.email && formik.errors.email)}
          />
          {formik.touched.email && formik.errors.email ? (
            <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-birth-date">{t("studio.clients.fields.birth_date")}</Label>
          <Input
            id="client-birth-date"
            name="birth_date"
            type="date"
            value={formik.values.birth_date ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.birth_date && formik.errors.birth_date)}
          />
          {formik.touched.birth_date && formik.errors.birth_date ? (
            <FormFeedback type="invalid">{formik.errors.birth_date}</FormFeedback>
          ) : (
            <FormText muted className="d-block mt-1">
              {t("clients.fields.birth_date.hint")}
            </FormText>
          )}
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Label htmlFor="client-document-type">{t("studio.clients.fields.document_type")}</Label>
          <Input
            id="client-document-type"
            name="document_type"
            type="select"
            value={formik.values.document_type}
            onChange={(event) => {
              formik.handleChange(event);
              formik.setFieldValue("document_number_display", "");
              formik.setFieldTouched("document_number_display", false);
            }}
            onBlur={formik.handleBlur}
            invalid={!!documentTypeError}
          >
            {CLIENT_DOCUMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`studio.clients.fields.document_types.${type}`)}
              </option>
            ))}
          </Input>
          {documentTypeError ? (
            <FormFeedback type="invalid">{documentTypeError}</FormFeedback>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-document-number">{documentNumberLabel}</Label>
          {useCpfMask ? (
            <Cleave
              id="client-document-number"
              className={`form-control${documentNumberError ? " is-invalid" : ""}`}
              placeholder={documentNumberPlaceholder}
              options={{
                delimiters: [".", ".", "-"],
                blocks: [3, 3, 3, 2],
                numericOnly: true,
              }}
              value={formik.values.document_number_display}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue("document_number_display", event.target.value)
              }
              onBlur={() => formik.setFieldTouched("document_number_display", true)}
            />
          ) : (
            <Input
              id="client-document-number"
              name="document_number_display"
              placeholder={documentNumberPlaceholder}
              value={formik.values.document_number_display}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!documentNumberError}
              maxLength={50}
            />
          )}
          {documentNumberError ? (
            <div className="invalid-feedback d-block">{documentNumberError}</div>
          ) : null}
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Label htmlFor="client-gender">{t("clients.fields.gender.label")}</Label>
          <Input
            id="client-gender"
            name="gender"
            type="select"
            value={formik.values.gender}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.gender && formik.errors.gender)}
          >
            <option value="">{t("clients.fields.gender.empty")}</option>
            {CLIENT_GENDERS.map((gender) => (
              <option key={gender} value={gender}>
                {t(`clients.fields.gender.${gender}`)}
              </option>
            ))}
          </Input>
          {formik.touched.gender && formik.errors.gender ? (
            <FormFeedback type="invalid">{String(formik.errors.gender)}</FormFeedback>
          ) : null}
        </Col>

        <Col md={4}>
          <Label htmlFor="client-instagram">{t("clients.fields.instagram.label")}</Label>
          <Input
            id="client-instagram"
            name="instagram_handle"
            placeholder={t("clients.fields.instagram.placeholder")}
            value={formik.values.instagram_handle ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.instagram_handle && formik.errors.instagram_handle)}
            maxLength={64}
            data-clarity-mask="true"
          />
          {formik.touched.instagram_handle && formik.errors.instagram_handle ? (
            <FormFeedback type="invalid">{String(formik.errors.instagram_handle)}</FormFeedback>
          ) : (
            <FormText muted className="d-block mt-1">
              {t("clients.fields.instagram.hint")}
            </FormText>
          )}
        </Col>

        <Col md={4}>
          <Label htmlFor="client-acquisition">
            {t("clients.fields.acquisition_source.label")}
          </Label>
          <Input
            id="client-acquisition"
            name="acquisition_source"
            placeholder={t("clients.fields.acquisition_source.placeholder")}
            value={formik.values.acquisition_source ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              !!(formik.touched.acquisition_source && formik.errors.acquisition_source)
            }
            maxLength={120}
          />
          {formik.touched.acquisition_source && formik.errors.acquisition_source ? (
            <FormFeedback type="invalid">
              {String(formik.errors.acquisition_source)}
            </FormFeedback>
          ) : (
            <FormText muted className="d-block mt-1">
              {t("clients.fields.acquisition_source.hint")}
            </FormText>
          )}
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Label htmlFor="client-source-registered-on">
            {t("clients.fields.source_registered_on.label")}
          </Label>
          <Input
            id="client-source-registered-on"
            name="source_registered_on"
            type="date"
            value={formik.values.source_registered_on ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              !!(formik.touched.source_registered_on && formik.errors.source_registered_on)
            }
          />
          {formik.touched.source_registered_on && formik.errors.source_registered_on ? (
            <FormFeedback type="invalid">
              {String(formik.errors.source_registered_on)}
            </FormFeedback>
          ) : (
            <FormText muted className="d-block mt-1">
              {t("clients.fields.source_registered_on.hint")}
            </FormText>
          )}
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <div className="form-check form-switch mb-2">
            <Input
              id="client-allow-online-booking"
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={formik.values.allow_online_booking}
              onChange={(event) =>
                formik.setFieldValue("allow_online_booking", event.target.checked)
              }
            />
            <Label className="form-check-label" htmlFor="client-allow-online-booking">
              {t("clients.fields.allow_online_booking.label")}
            </Label>
            <FormText muted className="d-block">
              {t("clients.fields.allow_online_booking.hint")}
            </FormText>
          </div>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <Button
            color="link"
            className="btn-link text-decoration-none p-0 d-flex align-items-center gap-1 text-body"
            type="button"
            onClick={() => {
              setContactPrefsOpen((open) => {
                const next = !open;
                try {
                  window.localStorage.setItem(
                    "adatis.clients.contactPrefsOpen",
                    next ? "1" : "0"
                  );
                } catch {
                  // ignore
                }
                return next;
              });
            }}
            aria-expanded={contactPrefsOpen}
            aria-controls="client-contact-prefs"
          >
            <i
              className={`ri-arrow-${contactPrefsOpen ? "down" : "right"}-s-line fs-16`}
              aria-hidden
            />
            <span className="fs-13 fw-semibold">{t("clients.form.section.contactPrefs")}</span>
          </Button>
          <FormText muted className="d-block mb-2 ms-4">
            {t("clients.form.section.contactPrefsHint")}
          </FormText>
          <Collapse isOpen={contactPrefsOpen} id="client-contact-prefs">
            <Row className="g-3">
              <Col md={4}>
                <div className="form-check form-switch">
                  <Input
                    id="client-accepts-appointment-email"
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.accepts_appointment_email}
                    onChange={(event) =>
                      formik.setFieldValue("accepts_appointment_email", event.target.checked)
                    }
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="client-accepts-appointment-email"
                  >
                    {t("clients.fields.accepts_appointment_email.label")}
                  </Label>
                </div>
              </Col>
              <Col md={4}>
                <div className="form-check form-switch">
                  <Input
                    id="client-accepts-marketing-email"
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.accepts_marketing_email}
                    onChange={(event) =>
                      formik.setFieldValue("accepts_marketing_email", event.target.checked)
                    }
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="client-accepts-marketing-email"
                  >
                    {t("clients.fields.accepts_marketing_email.label")}
                  </Label>
                </div>
              </Col>
              <Col md={4}>
                <div className="form-check form-switch">
                  <Input
                    id="client-accepts-sms-marketing"
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.accepts_sms_marketing}
                    onChange={(event) =>
                      formik.setFieldValue("accepts_sms_marketing", event.target.checked)
                    }
                  />
                  <Label className="form-check-label" htmlFor="client-accepts-sms-marketing">
                    {t("clients.fields.accepts_sms_marketing.label")}
                  </Label>
                </div>
              </Col>
            </Row>
          </Collapse>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <Label htmlFor="client-notes">{t("clients.form.notesLabel")}</Label>
          <Input
            id="client-notes"
            name="notes"
            type="textarea"
            rows={8}
            style={{ minHeight: 160 }}
            placeholder={t("clients.form.notesPlaceholder")}
            value={formik.values.notes ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.notes && formik.errors.notes)}
            data-clarity-mask="true"
          />
          <FormText muted className="d-block mt-1">
            {t("clients.form.notesHint")}
          </FormText>
          <FormText muted className="d-block">
            {t("clients.form.notesCounter", {
              count: (formik.values.notes ?? "").length,
              max: CLIENT_OPERATIONAL_NOTES_MAX_LENGTH,
            })}
          </FormText>
          {formik.touched.notes && formik.errors.notes ? (
            <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>
          ) : null}
        </Col>
      </Row>

      <Row className="g-3 mb-1">
        <Col md={12}>
          <h6 className="fs-13 mb-1">{t("clients.preferences.sectionTitle")}</h6>
          <FormText muted className="d-block mb-2">
            {t("clients.preferences.sectionHint")}
          </FormText>
        </Col>
        <Col md={6}>
          <Label htmlFor="client-pref-professional">
            {t("clients.preferences.preferredProfessional")}
          </Label>
          <Input
            id="client-pref-professional"
            type="select"
            value={formik.values.service_preferences.preferred_professional_id}
            onChange={(event) =>
              formik.setFieldValue(
                "service_preferences.preferred_professional_id",
                event.target.value
              )
            }
            onBlur={() =>
              formik.setFieldTouched(
                "service_preferences.preferred_professional_id",
                true
              )
            }
          >
            <option value="">{t("clients.preferences.preferredProfessionalNone")}</option>
            {professionals.map((professional) => (
              <option key={professional.id} value={String(professional.id)}>
                {professional.name}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={6}>
          <Label htmlFor="client-pref-haircut">
            {t("clients.preferences.haircutStyle")}
          </Label>
          <Input
            id="client-pref-haircut"
            value={formik.values.service_preferences.haircut_style}
            maxLength={CLIENT_PREF_MAX_SHORT}
            placeholder={t("clients.preferences.haircutStylePlaceholder")}
            onChange={(event) =>
              formik.setFieldValue(
                "service_preferences.haircut_style",
                event.target.value
              )
            }
            data-clarity-mask="true"
          />
        </Col>
        <Col md={4}>
          <Label htmlFor="client-pref-guard">{t("clients.preferences.guard")}</Label>
          <Input
            id="client-pref-guard"
            value={formik.values.service_preferences.guard}
            maxLength={CLIENT_PREF_MAX_SHORT}
            placeholder={t("clients.preferences.guardPlaceholder")}
            onChange={(event) =>
              formik.setFieldValue("service_preferences.guard", event.target.value)
            }
            data-clarity-mask="true"
          />
        </Col>
        <Col md={8}>
          <Label htmlFor="client-pref-beard">
            {t("clients.preferences.beardNotes")}
          </Label>
          <Input
            id="client-pref-beard"
            value={formik.values.service_preferences.beard_notes}
            maxLength={CLIENT_PREF_MAX_NOTE}
            placeholder={t("clients.preferences.beardNotesPlaceholder")}
            onChange={(event) =>
              formik.setFieldValue(
                "service_preferences.beard_notes",
                event.target.value
              )
            }
            data-clarity-mask="true"
          />
        </Col>
        <Col md={6}>
          <Label htmlFor="client-pref-postcare">
            {t("clients.preferences.postCareNotes")}
          </Label>
          <Input
            id="client-pref-postcare"
            type="textarea"
            rows={2}
            value={formik.values.service_preferences.post_care_notes}
            maxLength={CLIENT_PREF_MAX_NOTE}
            placeholder={t("clients.preferences.postCareNotesPlaceholder")}
            onChange={(event) =>
              formik.setFieldValue(
                "service_preferences.post_care_notes",
                event.target.value
              )
            }
            data-clarity-mask="true"
          />
        </Col>
        <Col md={6}>
          <Label htmlFor="client-pref-protocol">
            {t("clients.preferences.protocolAlert")}
          </Label>
          <Input
            id="client-pref-protocol"
            type="textarea"
            rows={2}
            value={formik.values.service_preferences.protocol_alert}
            maxLength={CLIENT_PREF_MAX_NOTE}
            placeholder={t("clients.preferences.protocolAlertPlaceholder")}
            onChange={(event) =>
              formik.setFieldValue(
                "service_preferences.protocol_alert",
                event.target.value
              )
            }
            data-clarity-mask="true"
          />
        </Col>
      </Row>
    </>
  );
};

export default ClientForm;
